import React, { useEffect, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
// Import child components from Common
import ActivityFeed from "Common/CommonFollowUpForEnquirySalesOrder/ActivityFeed";
import AssignedMembers from "Common/CommonFollowUpForEnquirySalesOrder/AssignedMembers";
import ScheduleFollowUp from "Common/CommonFollowUpForEnquirySalesOrder/ScheduleFollowUp";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import {
    createEnquiryFollowUp,
    fetchFollowUpsByType,
} from "slices/enquiry/thunk";
import { createFollowUpByType } from "slices/FollowUp/thunk";
import { Grid, Accordion, AccordionDetails, AccordionSummary, Typography } from "@mui/material";
import moment from "moment";
import { formatIndianAmount } from "helpers/formatIndianAmount";

interface AssignedMember {
    id: string; // ID of the assignment record
    user: string; // User ID
    username: string; // Display Name
    initials: string; // Calculated initials
    bg: string; // Display color
    color: string; // Display text color
}

interface Activity {
    id: string;
    user: string;
    date: string;
    text: string;
    initials: string;
    bg: string;
    color: string;
    follow_up_type_name: string;
}

const HARDCODED_FOLLOW_UP_TYPES = [
    { id: "call", name: "Call" },
    { id: "message", name: "Message" },
    { id: "email", name: "Email" },
    { id: "meeting", name: "Meeting" },
    { id: "onsitevisit", name: "On Site Visit" },
];

// In Retention/PBG follow-ups, the API may include an initial record with no `follow_up_date`.
// We must hide ONLY the first such record from the Comments/Activity feed.
const excludeFirstNoDateFollowUp = (items: any[]): any[] => {
    const idx = items.findIndex((x) => !x?.follow_up_date);
    if (idx === -1) return items;
    return [...items.slice(0, idx), ...items.slice(idx + 1)];
};

const AVATAR_COLORS = [
    { bg: "#FFCDD2", color: "#B71C1C" }, // red
    { bg: "#C8E6C9", color: "#1B5E20" }, // green
    { bg: "#BBDEFB", color: "#0D47A1" }, // blue
    { bg: "#FFF9C4", color: "#F57F17" }, // yellow
    { bg: "#D1C4E9", color: "#4A148C" }, // purple
    { bg: "#FFE0B2", color: "#E65100" }, // orange
    { bg: "#B2EBF2", color: "#006064" }, // cyan
    { bg: "#F8BBD0", color: "#880E4F" }, // pink
    { bg: "#DCEDC8", color: "#33691E" }, // lime
    { bg: "#CFD8DC", color: "#263238" }, // blue-grey
];

// --- Date helpers (required flow: local UI ↔ UTC backend) ---
const pad2 = (n: number) => String(n).padStart(2, "0");

// IMPORTANT: `ActivityFeed` parses `activity.date` with `new Date(...)`.
// To avoid double-parsing / timezone drift, always keep `activity.date` as ISO (UTC) from API.

// Convert `datetime-local` (local) → UTC ISO for backend storage.
const localDateTimeToUtcIso = (localDateTime: string): string => {
    if (!localDateTime) return "";
    const d = new Date(localDateTime); // interpreted as local time
    return Number.isNaN(d.getTime()) ? localDateTime : d.toISOString();
};

// Convert `date` (local day) → UTC ISO (local midnight converted to UTC).
const localDateToUtcIso = (localDate: string): string => {
    if (!localDate) return "";
    const [y, m, day] = localDate.split("-").map(Number);
    if (!y || !m || !day) return localDate;
    const d = new Date(y, m - 1, day, 0, 0, 0);
    return Number.isNaN(d.getTime()) ? localDate : d.toISOString();
};

const SOURCE_ENTITY_ID_MAP: Record<string, string> = {
    QUOTATION_PRODUCT: "quotation_product_id",
    QUOTATION_SERVICE: "quotation_service_id",

    ENQUIRY_PRODUCT: "enquiry_product_id",
    ENQUIRY_SERVICE: "enquiry_service_id",

    SALES_ORDER_PRODUCT: "sales_order_product_id",
    SALES_ORDER_SERVICE: "sales_order_service_id",
};

interface FollowupModelProps {
    data: any;
    activeType: string;
    category?: string;
    onClose?: () => void;
    showAssignedMembers?: boolean;
}

const FollowupModel: React.FC<FollowupModelProps> = ({
    data,
    activeType,
    category,
    onClose,
    showAssignedMembers = true,
}) => {
    const dispatch = useDispatch<any>();
    const [assignedMembers, setAssignedMembers] = useState<AssignedMember[]>([]);
    const [activities, setActivities] = useState<Activity[]>([]);
    const [followUpData, setFollowUpData] = useState({
        followUpType: "",
        dateTime: "",
        nextFollowUpDate: "",
        attachmentFiles: [] as File[],
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [loading, setLoading] = useState(false);
    const [editorContent, setEditorContent] = useState("");
    const [editorResetKey, setEditorResetKey] = useState(0);

    // Enforce "Follow up Date" >= last follow-up date (if any).
    // `activities[].date` is stored as UTC ISO from API.
    const lastFollowUpUtcIso: string | undefined = React.useMemo(() => {
        const maxTime = activities.reduce<number>((acc, a) => {
            const t = a?.date ? Date.parse(a.date) : NaN;
            return Number.isFinite(t) ? Math.max(acc, t) : acc;
        }, -Infinity);

        return Number.isFinite(maxTime) ? new Date(maxTime).toISOString() : undefined;
    }, [activities]);

    const minFollowUpDateTimeLocal: string | undefined = React.useMemo(() => {
        if (!lastFollowUpUtcIso) return undefined;
        const d = new Date(lastFollowUpUtcIso); // converts to local time getters
        if (Number.isNaN(d.getTime())) return undefined;

        // datetime-local format: YYYY-MM-DDTHH:mm (local)
        return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}T${pad2(
            d.getHours()
        )}:${pad2(d.getMinutes())}`;
    }, [lastFollowUpUtcIso]);

    // Map Activites
    const mapFollowUpToActivity = (item: any) => {
        const firstName = item?.created_by_user?.first_name || "";
        const lastName = item?.created_by_user?.last_name || "";
        const fullName = `${firstName} ${lastName}`.trim() || "Unknown User";
        const initials = `${firstName[0] || ""}${lastName[0] || ""}`.toUpperCase();

        // 🔹 Deterministic color selection
        const colorSeed = (firstName + lastName).charCodeAt(0) || 0;
        const colorIndex = colorSeed % AVATAR_COLORS.length;
        const { bg, color } = AVATAR_COLORS[colorIndex];

        return {
            id: item.follow_up_id,
            user: fullName,
            date: item.follow_up_date || "",
            text: item.follow_up_remarks || "",
            initials,
            bg,
            color,
            follow_up_type_name:
                item.follow_up_type?.charAt(0).toUpperCase() +
                item.follow_up_type?.slice(1),
        };
    };

    // Map Members
    const mapAssignedMember = (item: any): AssignedMember => {
        console.log("ITEM NAME", item);
        const firstName = item.first_name || "";
        const lastName = item.last_name || "";
        const fullName =
            `${firstName} ${lastName}`.trim() || item.username || "Unknown User";
        const initials = `${firstName[0] || ""}${lastName[0] || ""}`.toUpperCase();
        // deterministic color (same user → same color)
        const colorIndex = initials.charCodeAt(0) % AVATAR_COLORS.length || 0;

        return {
            id: item.assigned_member_id,
            user: item.user_id,
            username: fullName,
            initials,
            bg: AVATAR_COLORS[colorIndex].bg,
            color: AVATAR_COLORS[colorIndex].color,
        };
    };

    // --- Handlers --- Date and time
    const handleInputChange = (
        e: React.ChangeEvent<
            HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
        >
    ) => {
        const { name, value, files } = e.target as HTMLInputElement;
        if (name === "attachments" && files) {
            setFollowUpData((prev) => ({
                ...prev,
                attachmentFiles: Array.from(files),
            }));
        } else {
            setFollowUpData((prev) => ({ ...prev, [name]: value }));
        }
    };

    const formatDate = (dateStr: string | null | undefined) => {
        if (!dateStr || dateStr === "-" || dateStr.trim() === "") return "-";
        try {
            // Handle ISO date strings (e.g., "2026-01-03T00:00:00.000Z")
            let date;
            if (dateStr.includes('T') && dateStr.endsWith('Z')) {
                // UTC date string - parse as UTC and convert to local
                date = moment.utc(dateStr).local();
            } else if (dateStr.includes('T')) {
                // ISO string without Z - parse normally
                date = moment(dateStr);
            } else {
                // Just a date string - parse it
                date = moment(dateStr);
            }
            
            if (!date.isValid()) {
                // Fallback: try to extract just the date part (YYYY-MM-DD)
                const dateMatch = dateStr.match(/(\d{4}-\d{2}-\d{2})/);
                if (dateMatch) {
                    date = moment(dateMatch[1], 'YYYY-MM-DD');
                }
                if (!date.isValid()) {
                    return dateStr; // Return original if still invalid
                }
            }
            // Format as DD-MMM-YYYY (e.g., 03-Jan-2026)
            return date.format("DD-MMM-YYYY");
        } catch (error) {
            console.error("Error formatting date:", error, dateStr);
            return dateStr;
        }
    };

    //   Submit Follow Up
    const handleSubmitFollowUp = async (e: React.FormEvent) => {
        e.preventDefault();

        // 🔹 Validations
        if (!followUpData.followUpType) {
            toast.error("Please select follow-up type");
            return;
        }

        if (!followUpData.dateTime) {
            toast.error("Please select follow-up date");
            return;
        }

        // Prevent scheduling in the past relative to last follow-up date.
        if (lastFollowUpUtcIso) {
            const selectedTime = new Date(followUpData.dateTime).getTime(); // local input
            const lastTime = new Date(lastFollowUpUtcIso).getTime(); // UTC ISO → absolute time
            if (
                Number.isFinite(selectedTime) &&
                Number.isFinite(lastTime) &&
                selectedTime < lastTime
            ) {
                toast.error("Follow up date cannot be earlier than the last follow up date");
                return;
            }
        }

        if (!editorContent || !editorContent.trim()) {
            toast.error("Please enter follow-up remarks");
            return;
        }

        setIsSubmitting(true);

        try {
            const formData = new FormData();

            const entityIdKey = SOURCE_ENTITY_ID_MAP[activeType];

            if (!entityIdKey || !data?.[entityIdKey]) {
                throw new Error("Invalid active type or missing entity id");
            }

            // 🔹 REQUIRED payload (exact match)
            formData.append("source_entity_type", activeType); // ENQUIRY_PRODUCT
            formData.append("source_entity_id", data?.[entityIdKey]);
            formData.append("follow_up_date", localDateTimeToUtcIso(followUpData.dateTime));
            formData.append(
                "next_follow_up_date",
                followUpData.nextFollowUpDate
                  ? localDateToUtcIso(followUpData.nextFollowUpDate)
                  : ""
            );
            formData.append("follow_up_type", followUpData.followUpType);
            formData.append("follow_up_remarks", editorContent.trim());
            if (category) {
                formData.append("follow_up_category", category);
            }

            const response = await dispatch(createFollowUpByType(formData)).unwrap();
            const newActivity = mapFollowUpToActivity(response.data);
            setActivities((prev) => [newActivity, ...prev]);
            toast.success("Follow-up created successfully");

            // 🔹 Reset form
            setFollowUpData({
                followUpType: "",
                dateTime: "",
                nextFollowUpDate: "",
                attachmentFiles: [],
            });
            setEditorContent("");
            // setEditingFollowUpId(null);
            setEditorResetKey((prev) => prev + 1);
        } catch (error: any) {
            console.error(error);
            toast.error(
                error?.response?.data?.message || "Failed to create follow-up"
            );
        } finally {
            setIsSubmitting(false);
        }
    };

    // Assigned Members from prop
    useEffect(() => {
        if (data?.assigned_members) {
            const uniqueMembers = Array.from(
                new Map(data.assigned_members.map((m: any) => [m.user_id, m])).values()
            );

            const mappedMembers = uniqueMembers.map(mapAssignedMember);
            console.log("MAPPED", mappedMembers);
            setAssignedMembers(mappedMembers);

            //   setAssignedMembers(uniqueMembers);
        }
    }, [data?.assigned_members]);

    // Fetch Initial FollowUp data API
    useEffect(() => {
        const fetchFollowUp = async () => {
            setLoading(true);
            try {
                const entityIdKey = SOURCE_ENTITY_ID_MAP[activeType];

                if (!entityIdKey || !data?.[entityIdKey]) {
                    throw new Error("Invalid active type or missing entity id");
                }
                const res = await dispatch(
                    fetchFollowUpsByType({
                        source_entity_type: activeType,
                        source_entity_id: data?.[entityIdKey],
                        source_category: category
                    })
                ).unwrap();
                const shouldExcludeFirstNoDate = ["retention", "pbg"].includes(
                    String(category ?? "").toLowerCase()
                );

                const followUpsRaw: any[] = Array.isArray(res?.data) ? res.data : [];
                const followUpsForFeed = shouldExcludeFirstNoDate
                    ? excludeFirstNoDateFollowUp(followUpsRaw)
                    : followUpsRaw;

                const activities = followUpsForFeed.map(mapFollowUpToActivity);
                setActivities(activities);
                console.log("RES for get api", res.data);
            } catch (error: any) {
                console.log("Error", error);
                toast.error(error?.error || error?.message);
            } finally {
                setLoading(false);
            }
        };
        fetchFollowUp();
    }, [dispatch, activeType, category, data]);

    useEffect(() => {
        console.log("DATA from FOLLOW UP", data);
    }, [data]);

    return (
        <div
            style={{
                height: "100%",
                padding: "24px",
                backgroundColor: "#fff",
                borderRadius: "16px",
                border: "1px solid #e9ebec",
            }}
        >
            <Accordion
                disableGutters
                elevation={0}
                sx={{
                    border: "1px solid #e9ebec",
                    borderRadius: "12px",
                    "&:before": { display: "none" },
                    mb: 3,
                }}
            >
                <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{
                        backgroundColor: "#fff",
                        borderBottom: "1px solid #f0f0f0",
                        minHeight: 56,
                        "& .MuiAccordionSummary-content": {
                            margin: 0,
                        },
                    }}
                >
                    <Typography fontWeight={600}>
                        PBG (Performance Bank Guarantee) Details
                    </Typography>
                </AccordionSummary>

                <AccordionDetails
                    sx={{
                        backgroundColor: "#fcfdfe",
                        p: 3,
                    }}
                >
                    <Grid container spacing={4}>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <div className="d-flex align-items-center mb-2">
                                <Typography
                                    variant="body2"
                                    sx={{ fontWeight: 600, color: "#666", width: "140px" }}
                                >
                                    PBG Applicable:
                                </Typography>
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                    {data?.pbg_applicable ? "Yes" : "No"}
                                </Typography>
                            </div>

                            <div className="d-flex align-items-center mb-2">
                                <Typography
                                    variant="body2"
                                    sx={{ fontWeight: 600, color: "#666", width: "140px" }}
                                >
                                    PBG Date:
                                </Typography>
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                    {formatDate(data?.pbg_date)}
                                </Typography>
                            </div>

                            <div className="d-flex align-items-center mb-2">
                                <Typography
                                    variant="body2"
                                    sx={{ fontWeight: 600, color: "#666", width: "140px" }}
                                >
                                    Validity:
                                </Typography>
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                    {formatDate(data?.validity)}
                                </Typography>
                            </div>

                            <div className="d-flex align-items-center">
                                <Typography
                                    variant="body2"
                                    sx={{ fontWeight: 600, color: "#666", width: "140px" }}
                                >
                                    PBG Status:
                                </Typography>
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                    {data?.pbg_status || "-"}
                                </Typography>
                            </div>
                        </Grid>

                        <Grid size={{ xs: 12, md: 6 }}>
                            <div className="d-flex align-items-center mb-2">
                                <Typography
                                    variant="body2"
                                    sx={{ fontWeight: 600, color: "#666", width: "140px" }}
                                >
                                    PBG Number:
                                </Typography>
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                    {data?.pbg_number || "-"}
                                </Typography>
                            </div>

                            <div className="d-flex align-items-center mb-2">
                                <Typography
                                    variant="body2"
                                    sx={{ fontWeight: 600, color: "#666", width: "140px" }}
                                >
                                    Bank Name:
                                </Typography>
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                    {data?.bank_name || "-"}
                                </Typography>
                            </div>

                            <div className="d-flex align-items-center mb-2">
                                <Typography
                                    variant="body2"
                                    sx={{ fontWeight: 600, color: "#666", width: "140px" }}
                                >
                                    Amount:
                                </Typography>
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                    {`${data?.currency_symbol || "₹"} ${formatIndianAmount(data?.pbg_amount)}`}
                                </Typography>
                            </div>

                            <div className="d-flex align-items-center mb-2">
                                <Typography
                                    variant="body2"
                                    sx={{ fontWeight: 600, color: "#666", width: "140px" }}
                                >
                                    Uploaded Document:
                                </Typography>
                                {data?.pbg_documents ? (
                                    <a
                                        href={data.pbg_documents}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        style={{ 
                                            fontWeight: 600, 
                                            fontSize: "0.875rem",
                                            color: "#1976d2",
                                            textDecoration: "none"
                                        }}
                                        onMouseEnter={(e) => e.currentTarget.style.textDecoration = "underline"}
                                        onMouseLeave={(e) => e.currentTarget.style.textDecoration = "none"}
                                    >
                                        View Document
                                    </a>
                                ) : (
                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                        -
                                    </Typography>
                                )}
                            </div>

                            {data?.uploaded_doc && (
                                <div className="d-flex align-items-center">
                                    <Typography
                                        variant="body2"
                                        sx={{ fontWeight: 600, color: "#666", width: "140px" }}
                                    >
                                        Uploaded Doc:
                                    </Typography>
                                    <a
                                        href={data?.uploaded_doc}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        style={{ fontWeight: 600, fontSize: "0.875rem", color: "#1976d2", textDecoration: "none" }}
                                        onMouseEnter={(e) => e.currentTarget.style.textDecoration = "underline"}
                                        onMouseLeave={(e) => e.currentTarget.style.textDecoration = "none"}
                                    >
                                        View Document
                                    </a>
                                </div>
                            )}
                        </Grid>
                    </Grid>
                </AccordionDetails>
            </Accordion>

            <Row className="g-5 h-100">
                {/* Left Column - Assigned Members & Schedule Follow-up */}
                <Col
                    md={6}
                    className="h-100 overflow-auto pr-md-4"
                    style={{ borderRight: "1px solid #f0f0f0" }}
                >
                    {/* Assigned Members Section */}
                    {showAssignedMembers && (assignedMembers && assignedMembers.length > 0) && (
                    <div className="mb-4">
                        <div className="fw-bold mb-3" style={{ fontSize: "1.25rem" }}>
                            Assigned Members
                        </div>
                        <AssignedMembers assignedMembers={assignedMembers || []} />
                    </div>
                    )}

                    {/* Schedule Follow-up Section */}
                    <div>
                        <div className="fw-bold mb-3" style={{ fontSize: "1.25rem" }}>
                            Follow-up
                        </div>
                        <ScheduleFollowUp
                            followUpData={followUpData}
                            handleSubmitFollowUp={handleSubmitFollowUp}
                            editorContent={editorContent}
                            setEditorContent={setEditorContent}
                            isSubmitting={isSubmitting}
                            handleInputChange={handleInputChange}
                            HARDCODED_FOLLOW_UP_TYPES={HARDCODED_FOLLOW_UP_TYPES}
                            minFollowUpDateTime={minFollowUpDateTimeLocal}
                            editorResetKey={editorResetKey}
                            onCancel={onClose}
                        />
                    </div>
                </Col>

                {/* Right Column - Activity Feed */}
                <Col md={6} className="d-flex flex-column h-100">
                    <h5 className="fw-bold mb-3" style={{ fontSize: "1.25rem" }}>
                        Comments and activity
                    </h5>
                    <div
                        className="flex-grow-1"
                        style={{ overflowY: "auto", minHeight: 0 }}
                    >
                        <ActivityFeed loading={loading} activities={activities} />
                    </div>
                </Col>
            </Row>
        </div>
    );
};

export default FollowupModel;
